import { useCallback } from 'react';

import { useSetAtom, useStore } from 'jotai';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  IncomeDataT,
  isDefined,
  ProgressT,
  RegistrationDataT,
} from '@consumer-frontend/nc-utils';

import {
  isInRegistrationFlow,
  registrationFlow,
  RouteConfigKeyT,
} from './applyRoutingSteps';
import {
  incomeDataAtom,
  registrationDataAtom,
} from '../store';

type UseApplyRoutingReturnT = {
  handleBack: () => Promise<void | Error>;
  handleContinue: (regData: Partial<RegistrationDataT>, incomeData?: Partial<IncomeDataT>) => Promise<void | Error>;
  progress: ProgressT;
};

export function useApplyRouting(): UseApplyRoutingReturnT {
  const navigate = useNavigate();
  const { pathname: currentRoute } = useLocation() as { pathname: RouteConfigKeyT; };
  const setRegistrationData = useSetAtom(registrationDataAtom);
  const setIncomeData = useSetAtom(incomeDataAtom);
  const store = useStore();
  const routeConfig = registrationFlow[currentRoute];
  const [current, total] = routeConfig?.progress() ?? [];
  const progress = { current: current ?? 0, total: total ?? 0 };

  const handleContinue = useCallback(
    async (regData: Partial<RegistrationDataT>, incomeData?: Partial<IncomeDataT>): Promise<void | Error> => {
      if (isInRegistrationFlow(currentRoute)) {
        // Note: it is important to update the reg data before getting next/back links
        // since the conditional routing sometimes relies on information just collected.
        setRegistrationData((currentData) => ({
          ...currentData,
          ...regData,
        }));

        if (incomeData !== undefined) {
          setIncomeData((currentData) => ({
            ...currentData,
            ...incomeData,
          }));
        }

        const context = { store };
        const nextRoute = routeConfig?.next(context);

        if (isDefined(nextRoute)) { navigate(nextRoute); }

        // If nextRoute is undefined, then it's the end of the reg flow.
        return Promise.resolve();
      }

      console.error(`Current route is not defined in the registration flow configuration: ${currentRoute}`);

      return Promise.reject(new Error('Invalid route'));
    },
    [
      currentRoute,
      navigate,
      routeConfig,
      setIncomeData,
      setRegistrationData,
      store,
    ],
  );

  const handleBack = useCallback(
    async (): Promise<void | Error> => {
      if (isInRegistrationFlow(currentRoute)) {
        const context = { store };
        const backRoute = routeConfig?.back(context);

        if (isDefined(backRoute)) { navigate(backRoute); }

        // If backRoute is undefined, then it's the start of the reg flow.
        return Promise.resolve();
      }

      console.error(`Current route is not defined in the registration flow configuration: ${currentRoute}`);

      return Promise.reject(new Error('Invalid route'));
    },
    [
      currentRoute,
      navigate,
      routeConfig,
      store,
    ],
  );

  return {
    handleBack,
    handleContinue,
    progress,
  };
}
